import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import WebPdfDocumentPreview from './WebPdfDocumentPreview';
import { downloadBlobFromUrl } from '../../../api/storage';
import { Sentry } from '../../../setupSentry';
import '../../../css/common/pdf/PdfPreview.css';

type PdfPreviewRequest = { url: string; fileName: string };
export type PdfPreviewInput = { url?: string; fileName?: string; mimeType?: string };
type PdfPreviewContextValue = { openPreview: (_input: PdfPreviewInput) => void };
type PdfBlobCache = { key: string; blob: Blob | null; promise: Promise<Blob> | null };

const PdfPreviewContext = createContext<PdfPreviewContextValue | null>(null);

export const isPdfPreviewable = ({
  fileName = '',
  mimeType = '',
  url = '',
}: PdfPreviewInput = {}): boolean => {
  const normalizedType = String(mimeType || '').toLowerCase();
  return normalizedType.includes('pdf') || /\.pdf(?:$|[?#])/i.test(fileName) || /\.pdf(?:$|[?#])/i.test(url);
};

const ensurePdfFilename = (value = ''): string => {
  const normalized = String(value || '').trim() || 'document.pdf';
  return /\.pdf$/i.test(normalized) ? normalized : `${normalized}.pdf`;
};

export const isPdfBlob = async (blob: unknown): Promise<boolean> => {
  if (!(blob instanceof Blob)) return false;
  if (blob.size < 12) return false;
  const prefix = await blob.slice(0, Math.min(blob.size, 1024)).text();
  if (!prefix.includes('%PDF-')) return false;
  const tailStart = Math.max(0, blob.size - 64 * 1024);
  const tail = await blob.slice(tailStart).text();
  const startXrefIndex = tail.lastIndexOf('startxref');
  const eofIndex = tail.lastIndexOf('%%EOF');
  if (startXrefIndex < 0 || eofIndex < startXrefIndex) return false;
  const xrefOffset = Number(tail.slice(startXrefIndex).match(/^startxref\s+(\d+)/)?.[1]);
  return Number.isSafeInteger(xrefOffset) && xrefOffset >= 0 && xrefOffset < blob.size;
};

const saveBlob = (blob: Blob, filename?: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensurePdfFilename(filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const canShareFile = (file: File | null): boolean => {
  if (!file || typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

export const PdfPreviewProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<PdfPreviewRequest | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loadSequenceRef = useRef(0);
  const cacheRef = useRef<PdfBlobCache>({ key: '', blob: null, promise: null });

  const closePreview = useCallback(() => {
    loadSequenceRef.current += 1;
    setRequest(null);
    setLoading(false);
    setError('');
    setPdfBlob(null);
  }, []);

  const openPreview = useCallback(({ url, fileName }: PdfPreviewInput = {}) => {
    if (!url) return;
    setRequest({ url, fileName: ensurePdfFilename(fileName) });
  }, []);

  const getPdfBlob = useCallback((currentRequest: PdfPreviewRequest): Promise<Blob> => {
    const key = `${currentRequest.url}|${currentRequest.fileName}`;
    const cached = cacheRef.current;
    if (cached.key === key && cached.blob) return Promise.resolve(cached.blob);
    if (cached.key === key && cached.promise) return cached.promise;

    const promise = (async () => {
      const downloaded = await downloadBlobFromUrl(currentRequest.url, 'include');
      if (!(await isPdfBlob(downloaded))) {
        Sentry.captureMessage('web_pdf_integrity_validation_failed', {
          level: 'warning',
          tags: { component: 'PdfPreviewProvider', stage: 'download_validation' },
        });
        throw new Error('PDFの取得が完了していないか、PDF形式として確認できませんでした。');
      }
      return downloaded.type === 'application/pdf'
        ? downloaded
        : new Blob([downloaded], { type: 'application/pdf' });
    })();
    cacheRef.current = { key, blob: null, promise };
    promise.then((blob) => {
      if (cacheRef.current.key === key && cacheRef.current.promise === promise) {
        cacheRef.current = { key, blob, promise: null };
      }
    }).catch(() => {
      if (cacheRef.current.key === key && cacheRef.current.promise === promise) {
        cacheRef.current = { key: '', blob: null, promise: null };
      }
    });
    return promise;
  }, []);

  const loadPdf = useCallback(async () => {
    if (!request?.url) return;
    const sequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequence;
    setLoading(true);
    setError('');
    setPdfBlob(null);
    try {
      const downloaded = await getPdfBlob(request);
      if (loadSequenceRef.current !== sequence) return;
      setPdfBlob(downloaded);
    } catch (loadError: unknown) {
      if (loadSequenceRef.current !== sequence) return;
      setError(loadError instanceof Error ? loadError.message : 'PDFを読み込めませんでした。');
    } finally {
      if (loadSequenceRef.current === sequence) setLoading(false);
    }
  }, [getPdfBlob, request]);

  useEffect(() => {
    if (request?.url) loadPdf();
  }, [loadPdf, request?.url]);

  useEffect(() => () => {
    loadSequenceRef.current += 1;
    cacheRef.current = { key: '', blob: null, promise: null };
  }, []);

  const shareFile = useMemo(() => {
    if (!pdfBlob || typeof File !== 'function') return null;
    return new File([pdfBlob], request?.fileName || 'document.pdf', { type: 'application/pdf' });
  }, [pdfBlob, request?.fileName]);
  const canShare = canShareFile(shareFile);

  const handleShare = useCallback(async () => {
    if (!canShare || !shareFile) return;
    try {
      await navigator.share({ files: [shareFile], title: request?.fileName || 'PDF' });
    } catch (shareError: unknown) {
      if (!(shareError instanceof DOMException && shareError.name === 'AbortError')) {
        setError(shareError instanceof Error ? shareError.message : 'PDFを共有できませんでした。');
      }
    }
  }, [canShare, request?.fileName, shareFile]);

  const contextValue = useMemo(() => ({ openPreview }), [openPreview]);
  const closeAction = (
    <DsButton
      variant="secondary"
      size="sm"
      icon
      aria-label="PDFプレビューを閉じる"
      title="閉じる"
      onClick={closePreview}
    >
      <i className="fa-solid fa-xmark" aria-hidden="true" />
    </DsButton>
  );
  const fileActions = pdfBlob ? (
    <>
      {canShare ? (
        <DsButton
          variant="secondary"
          size="sm"
          icon
          aria-label="共有"
          title="共有"
          onClick={handleShare}
        >
          <i className="fa-solid fa-share-nodes" aria-hidden="true" />
        </DsButton>
      ) : null}
      <DsButton
        variant="primary"
        size="sm"
        icon
        aria-label="保存"
        title="保存"
        onClick={() => saveBlob(pdfBlob, request?.fileName)}
      >
        <i className="fa-solid fa-download" aria-hidden="true" />
      </DsButton>
      {closeAction}
    </>
  ) : closeAction;

  return (
    <PdfPreviewContext.Provider value={contextValue}>
      {children}
      <DsModal
        open={Boolean(request)}
        onClose={closePreview}
        title={undefined}
        ariaLabel={`${request?.fileName || 'PDF'}のプレビュー`}
        footer={undefined}
        showClose={false}
        size="lg"
        presentation="fullscreen"
        overlayClassName="pdf-preview-overlay"
        className="pdf-preview"
        bodyClassName="pdf-preview__body"
      >
        {loading ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{closeAction}</div>
            </div>
            <div className="pdf-preview__state" role="status">PDFを読み込んでいます...</div>
          </div>
        ) : error ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{pdfBlob ? fileActions : closeAction}</div>
            </div>
            <div className="pdf-preview__state pdf-preview__state--error" role="alert">
              <p>{error}</p>
              {pdfBlob ? (
                <p className="pdf-preview__error-guidance">
                  表示できない場合も、［保存］から原本のPDFを確認できます。
                </p>
              ) : null}
              <DsButton variant="secondary" onClick={loadPdf}>再試行</DsButton>
            </div>
          </div>
        ) : pdfBlob ? (
          <WebPdfDocumentPreview
            blob={pdfBlob}
            fileName={request?.fileName || 'document.pdf'}
            onError={setError}
            toolbarActions={fileActions}
          />
        ) : null}
      </DsModal>
    </PdfPreviewContext.Provider>
  );
};

export const usePdfPreview = () => useContext(PdfPreviewContext);
